import { User, Building2, UserCircle2, Calendar, FileText, Lock } from "lucide-react";

export function resolveVars(text = "", proposal) {
  const md = proposal?.metadata || {};
  const map = {
    Client: md.customer_name || proposal?.prospect_name || "",
    Company: md.company || proposal?.company_name || "",
    Date: md.proposal_date || "",
    ProposalTitle: proposal?.title || "",
    PreparedBy: md.prepared_by || md.proposal_owner || "",
    Pricing: md.pricing || proposal?.total_value || "",
  };
  return String(text).replace(/\{\{\s*(\w+)\s*\}\}/g, (_, k) => (k in map ? map[k] : "{{" + k + "}}"));
}

export default function CoverPage({ proposal, setProposal, onEdit }) {
  const md = proposal.metadata || {};
  const set = (patch) => setProposal({ ...proposal, metadata: { ...md, ...patch } });

  const rows = [
    { key: "customer_name", icon: <User size={16} className="text-[#011B90]" />, label: "Prepared For", value: md.customer_name },
    { key: "company", icon: <Building2 size={16} className="text-[#011B90]" />, label: "Company", value: md.company || proposal.company_name },
    { key: "prepared_by", icon: <UserCircle2 size={16} className="text-[#011B90]" />, label: "Prepared By", value: md.prepared_by || md.proposal_owner },
    { key: "proposal_date", icon: <Calendar size={16} className="text-[#011B90]" />, label: "Date", value: md.proposal_date },
    { key: "version", icon: <FileText size={16} className="text-[#011B90]" />, label: "Proposal Version", value: md.version || "v1.0" },
  ];

  return (
    <div className="relative bg-white rounded-2xl border border-[#E8EAF3] editor-shell overflow-hidden" data-testid="cover-page">
      {/* Big watermark Q */}
      <svg viewBox="0 0 400 400" className="absolute right-[-40px] top-32 pointer-events-none opacity-[0.045]" width="520" height="520" aria-hidden="true">
        <circle cx="200" cy="200" r="140" fill="none" stroke="#0B1736" strokeWidth="42" />
        <path d="M280 280 L360 360" stroke="#0B1736" strokeWidth="42" strokeLinecap="round" />
      </svg>

      {/* Header */}
      <div className="px-14 pt-12 pb-6 relative">
        <img data-testid="cover-brand-logo" src="/quowin-logo.png" alt="QuoWin" className="h-9 w-auto" />
      </div>

      {/* Purple divider */}
      <div className="mx-14 h-[3px] rounded-full bg-gradient-to-r from-[#011B90] via-[#5A6BB7] to-[#011B90]" />

      {/* Title block */}
      <div className="px-14 pt-14 pb-6 relative">
        <div className="text-[11px] tracking-[0.24em] uppercase font-bold text-[#011B90] mb-4">Proposal</div>
        <input
          data-testid="cover-title"
          value={proposal.title || ""}
          onChange={(e) => setProposal({ ...proposal, title: e.target.value })}
          placeholder="Proposal Title"
          className="w-full font-display text-5xl md:text-6xl font-bold tracking-tight text-[#0B1736] bg-transparent outline-none focus:bg-[#EDEFF7]/50 rounded-md -mx-2 px-2"
        />
        <textarea
          data-testid="cover-subtitle"
          value={md.subtitle || ""}
          onChange={(e) => set({ subtitle: e.target.value })}
          placeholder="A short one-line description of the solution or engagement to summarize the value we bring."
          rows={2}
          className="mt-4 w-full text-[15px] text-[#5F6677] leading-relaxed bg-transparent outline-none resize-none focus:bg-[#EDEFF7]/50 rounded-md -mx-2 px-2"
        />
      </div>

      {/* Metadata rows */}
      <div className="px-14 pb-10 relative">
        <div className="max-w-lg space-y-0">
          {rows.map((r) => (
            <div key={r.key} className="flex items-center gap-4 py-4 border-b border-[#E8EAF3] last:border-0">
              <div className="h-9 w-9 rounded-md bg-[#EDEFF7] grid place-items-center shrink-0">{r.icon}</div>
              <div className="w-40 text-[13px] font-semibold text-[#0B1736]">{r.label}</div>
              <div className="text-[#9098A8]">:</div>
              <input
                data-testid={`cover-${r.key}`}
                value={r.value || ""}
                onChange={(e) => r.key === "company"
                  ? (set({ company: e.target.value }), setProposal({ ...proposal, company_name: e.target.value, metadata: { ...md, company: e.target.value } }))
                  : set({ [r.key]: e.target.value })}
                placeholder="—"
                className="flex-1 text-[14px] text-[#0B1736] bg-transparent outline-none focus:bg-[#EDEFF7]/50 rounded-md px-2 py-1 -mx-2"
              />
            </div>
          ))}
        </div>
      </div>

      {/* Footer band */}
      <div className="mt-2 border-t border-[#E8EAF3] px-14 py-5 flex items-center justify-between text-[12px] text-[#5F6677]">
        <div className="flex items-center gap-2">
          <Lock size={13} className="text-[#0B1736]" />
          <span className="font-medium text-[#0B1736]">Confidential</span>
          <span className="text-[#9098A8]">— prepared exclusively for {md.company || proposal.company_name || "the recipient"}.</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-[#9098A8]">Page 1 of —</span>
        </div>
      </div>

      {/* Bottom purple bar */}
      <div className="h-2 bg-gradient-to-r from-[#011670] via-[#011B90] to-[#5A6BB7]" />
    </div>
  );
}
